import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack,
  ArticleOutlined,
  BusinessOutlined,
  CategoryOutlined,
  ChevronRight,
  Close,
  ContactPageOutlined,
  ExpandMore,
  LocalShippingOutlined,
  ManageSearchOutlined,
  PersonOutline,
  TranslateOutlined,
  Replay,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { Category, fetchCategories } from '../../store/slices/catalogSlice';
import { useRequestModal } from '../../contexts/RequestModalContext';
import MobileMenuActionDock from './MobileMenuActionDock';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { useAuth } from '../../contexts/AuthContext';

interface NavBarMobileProps {
  drawerOpen: boolean;
  toggleDrawer: (open: boolean) => void;
}

interface MobileNavLink {
  to: string;
  label: string;
  icon: React.ReactElement;
  external?: boolean;
}

const baseNavLinks: MobileNavLink[] = [
  { to: '/account', label: 'Личный кабинет', icon: <PersonOutline /> },
  { to: '/company', label: 'Компания', icon: <BusinessOutlined /> },
  { to: '/contacts', label: 'Контакты', icon: <ContactPageOutlined /> },
  {
    to: '/how-to-buy',
    label: 'Доставка и оплата',
    icon: <LocalShippingOutlined />,
  },
  {
    to: '/order-status',
    label: 'Статус заказа',
    icon: <ManageSearchOutlined />,
  },
  {
    to: 'https://yuzhen.store/blog/',
    label: 'Блог',
    icon: <ArticleOutlined />,
    external: true,
  },
];

const NavBarMobile: React.FC<NavBarMobileProps> = ({
  drawerOpen,
  toggleDrawer,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const gold = theme.palette.primary.main;
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal } = useRequestModal();
  const [isCatalogView, setIsCatalogView] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const { categories, loading, error } = useAppSelector(
    (state) => state.catalog
  );

  const navLinks: MobileNavLink[] = [
    ...(user ? [{ to: '/chinese', label: 'Китайский язык', icon: <TranslateOutlined /> }] : []),
    ...baseNavLinks,
  ];

  const resetMenuState = useCallback(() => {
    setIsCatalogView(false);
    setExpandedIds(new Set());
  }, []);

  const handleCloseDrawer = useCallback(() => {
    resetMenuState();
    toggleDrawer(false);
  }, [resetMenuState, toggleDrawer]);

  useEffect(() => {
    if (!drawerOpen) {
      resetMenuState();
    }
  }, [drawerOpen, resetMenuState]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const goToCategory = (category: Category) => {
    handleCloseDrawer();
    navigate(`/catalog/${category.id}`);
  };
  const handleBackClick = () => {
    setIsCatalogView(false);
    setExpandedIds(new Set());
  };

  const handleCatalogClick = () => {
    dispatch(fetchCategories());
    setIsCatalogView(true);
  };

  const retryCatalog = () => {
    dispatch(fetchCategories());
  };

  const mainRowSx = {
    minHeight: 48,
    mb: 0.5,
    px: 1.25,
    borderRadius: '13px',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: alpha(gold, 0.1),
    },
    '&.Mui-selected': {
      backgroundColor: alpha(gold, 0.14),
      color: gold,
    },
    '&.Mui-selected:hover': {
      backgroundColor: alpha(gold, 0.18),
    },
  } as const;

  const renderMainMenu = () => (
    <List disablePadding aria-label="Основное меню">
      <ListItemButton
        onClick={handleCatalogClick}
        sx={{
          ...mainRowSx,
          color: theme.palette.getContrastText(gold),
          background: `linear-gradient(135deg, ${gold}, ${theme.palette.primary.light})`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${gold})`,
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
          <CategoryOutlined />
        </ListItemIcon>
        <ListItemText
          primary="Каталог"
          primaryTypographyProps={{ fontWeight: 800 }}
        />
        <ChevronRight fontSize="small" />
      </ListItemButton>

      {navLinks.map((link) => {
        const selected = !link.external && location.pathname === link.to;
        const content = (
          <>
            <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
              {link.icon}
            </ListItemIcon>
            <ListItemText
              primary={link.label}
              primaryTypographyProps={{ fontWeight: 650 }}
            />
            <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
          </>
        );

        return link.external ? (
          <ListItemButton
            key={link.to}
            component="a"
            href={link.to}
            onClick={handleCloseDrawer}
            sx={mainRowSx}
          >
            {content}
          </ListItemButton>
        ) : (
          <ListItemButton
            key={link.to}
            component={RouterLink}
            to={link.to}
            selected={selected}
            onClick={handleCloseDrawer}
            sx={mainRowSx}
          >
            {content}
          </ListItemButton>
        );
      })}
    </List>
  );

  const renderCatalogState = () => {
    if (loading && categories.length === 0) {
      return (
        <Box sx={{ minHeight: 180, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={28} aria-label="Загрузка каталога" />
        </Box>
      );
    }

    if (error && categories.length === 0) {
      return (
        <Box sx={{ p: 1 }}>
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
          <Button fullWidth startIcon={<Replay />} onClick={retryCatalog}>
            Повторить
          </Button>
        </Box>
      );
    }

    if (categories.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Категории пока недоступны
          </Typography>
          <Button startIcon={<Replay />} onClick={retryCatalog}>
            Повторить
          </Button>
        </Box>
      );
    }
    return (
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {categories.map((category) => {
          const children = category.children ?? [];
          const hasChildren = children.length > 0;
          const expanded = expandedIds.has(category.id);
          const regionId = `mobile-category-${category.id}`;

          return (
            <Box component="li" key={category.id} sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() =>
                  hasChildren
                    ? toggleExpanded(category.id)
                    : goToCategory(category)
                }
                aria-expanded={hasChildren ? expanded : undefined}
                aria-controls={hasChildren ? regionId : undefined}
                sx={{
                  minHeight: 48,
                  px: 1.25,
                  borderRadius: '13px',
                  color: expanded ? gold : theme.palette.text.primary,
                  '&:hover': { backgroundColor: alpha(gold, 0.1) },
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 10,
                    height: 10,
                    mr: 1.5,
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: `2px solid ${gold}`,
                    backgroundColor: expanded ? gold : 'transparent',
                  }}
                />
                <ListItemText
                  primary={category.name}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: '0.92rem',
                  }}
                />
                {hasChildren ? (
                  <ExpandMore
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 200ms ease',
                    }}
                  />
                ) : (
                  <ChevronRight fontSize="small" />
                )}
              </ListItemButton>
              {hasChildren && (
                <Collapse in={expanded} timeout={220} unmountOnExit>
                  <Box
                    id={regionId}
                    component="ul"
                    sx={{
                      m: 0,
                      ml: 2.5,
                      pl: 1,
                      pb: 0.5,
                      listStyle: 'none',
                      borderLeft: `1px solid ${alpha(gold, 0.25)}`,
                    }}
                  >
                    {children.map((child) => (
                      <Box component="li" key={child.id}>
                        <ListItemButton
                          component={RouterLink}
                          to={`/catalog/${child.id}`}
                          onClick={handleCloseDrawer}
                          sx={{
                            minHeight: 44,
                            px: 1.25,
                            borderRadius: '11px',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: gold,
                              backgroundColor: alpha(gold, 0.08),
                            },
                          }}
                        >
                          <ListItemText
                            primary={child.name}
                            primaryTypographyProps={{
                              fontSize: '0.84rem',
                              lineHeight: 1.3,
                            }}
                          />
                        </ListItemButton>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={handleCloseDrawer}
      sx={{ zIndex: theme.zIndex.snackbar + 1 }}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: 'min(360px, 92vw)',
          maxWidth: '92vw',
          height: '100dvh',
          overflow: 'hidden',
          backgroundImage: 'none',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          component="header"
          sx={{
            minHeight: 72,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          {isCatalogView && (
            <IconButton
              onClick={handleBackClick}
              aria-label="Назад в основное меню"
            >
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 800 }}>
            {isCatalogView ? 'Каталог' : 'Меню'}
          </Typography>
          <Box sx={{ '& .MuiIconButton-root': { width: 40, height: 40 } }}>
            <ThemeToggle />
          </Box>
          <IconButton onClick={handleCloseDrawer} aria-label="Закрыть меню">
            <Close />
          </IconButton>
        </Box>

        <Divider />
        <Box
          component="nav"
          aria-label={
            isCatalogView ? 'Категории каталога' : 'Мобильная навигация'
          }
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: 1.5,
            py: 1,
            overscrollBehavior: 'contain',
          }}
        >
          {isCatalogView ? renderCatalogState() : renderMainMenu()}
        </Box>

        <MobileMenuActionDock
          drawerOpen={drawerOpen}
          onCloseDrawer={handleCloseDrawer}
          onRequest={openModal}
        />
      </Box>
    </Drawer>
  );
};

export default NavBarMobile;
